import React from 'react';
import { Helmet } from 'react-helmet';

const HealthCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-green-100 to-teal-100">
      <Helmet>
        <title>Health Calculators | Comprehensive Health & Wellness Tools</title>
        <meta name="description" content="Collection of 220+ health calculators for fitness, nutrition, medical conditions, and overall wellness." />
        <meta name="keywords" content="health calculators, fitness calculators, medical calculators, nutrition calculators, wellness tools" />
        <meta property="og:title" content="Health Calculators | Comprehensive Health & Wellness Tools" />
        <meta property="og:description" content="Collection of 220+ health calculators for fitness, nutrition, medical conditions, and overall wellness." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Health Calculators | Comprehensive Health & Wellness Tools" />
        <meta name="twitter:description" content="Collection of 220+ health calculators for fitness, nutrition, medical conditions, and overall wellness." />
        <link rel="canonical" href="https://yourdomain.com/health-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Health%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-green-600 mb-4">⚕️</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Health Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">220+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Explore our comprehensive collection of health calculators designed to help you monitor your fitness, nutrition, medical conditions, and overall wellness. These tools provide valuable insights to support your health journey.
            </p>
            <button className="read-more inline-block bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Fitness <span className="emoji ml-2">💪</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/bmi" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">BMI Calculator</h3>
                <p className="text-gray-600">Calculate your Body Mass Index</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/bmr" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">BMR Calculator</h3>
                <p className="text-gray-600">Calculate your Basal Metabolic Rate</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/tdee" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">TDEE Calculator</h3>
                <p className="text-gray-600">Estimate your Total Daily Energy Expenditure</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/body-fat-percentage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Body Fat Percentage Calculator</h3>
                <p className="text-gray-600">Calculate your body fat percentage</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/calorie" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Calorie Calculator</h3>
                <p className="text-gray-600">Estimate your daily calorie needs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/max-heart-rate" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Max Heart Rate Calculator</h3>
                <p className="text-gray-600">Calculate your maximum heart rate</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Nutrition <span className="emoji ml-2">🍎</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/protein-intake" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Protein Intake Calculator</h3>
                <p className="text-gray-600">Determine your daily protein requirements</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/hydration" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Hydration Calculator</h3>
                <p className="text-gray-600">Calculate your daily water needs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/meal-calorie" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Meal Calorie Calculator</h3>
                <p className="text-gray-600">Estimate calories in your meals</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/macro-nutrients" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Macro Nutrients Calculator</h3>
                <p className="text-gray-600">Calculate your macronutrient needs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/vitamin-d" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Vitamin D Calculator</h3>
                <p className="text-gray-600">Determine your vitamin D requirements</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/sugar-intake" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Sugar Intake Calculator</h3>
                <p className="text-gray-600">Calculate your daily sugar limits</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Medical <span className="emoji ml-2">🏥</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/blood-alcohol" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Blood Alcohol Calculator</h3>
                <p className="text-gray-600">Estimate blood alcohol concentration</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/blood-pressure" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Blood Pressure Calculator</h3>
                <p className="text-gray-600">Interpret your blood pressure readings</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/cholesterol" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Cholesterol Calculator</h3>
                <p className="text-gray-600">Analyze your cholesterol levels</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/glucose" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Blood Glucose Calculator</h3>
                <p className="text-gray-600">Interpret blood glucose readings</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/pregnancy-due-date" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pregnancy Due Date Calculator</h3>
                <p className="text-gray-600">Estimate your due date</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/medication-dosage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Medication Dosage Calculator</h3>
                <p className="text-gray-600">Calculate correct medication doses</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Mental Health <span className="emoji ml-2">🧠</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/sleep" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Sleep Calculator</h3>
                <p className="text-gray-600">Determine your optimal sleep schedule</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/stress-level" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Stress Level Calculator</h3>
                <p className="text-gray-600">Assess your stress levels</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mood-tracker" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mood Tracker Calculator</h3>
                <p className="text-gray-600">Track and analyze your mood patterns</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/anxiety-scores" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Anxiety Scores Calculator</h3>
                <p className="text-gray-600">Assess anxiety levels</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/depression-scores" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Depression Scores Calculator</h3>
                <p className="text-gray-600">Assess depression symptoms</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mindfulness" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mindfulness Calculator</h3>
                <p className="text-gray-600">Track your mindfulness practice</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Preventive Health <span className="emoji ml-2">🛡️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/cancer-risk" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Cancer Risk Calculator</h3>
                <p className="text-gray-600">Assess your cancer risk factors</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/heart-disease-risk" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Heart Disease Risk Calculator</h3>
                <p className="text-gray-600">Evaluate your cardiovascular risk</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/diabetes-risk" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Diabetes Risk Calculator</h3>
                <p className="text-gray-600">Assess your diabetes risk factors</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/osteoporosis-risk" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Osteoporosis Risk Calculator</h3>
                <p className="text-gray-600">Determine your bone health risks</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/immunization-schedule" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Immunization Schedule Calculator</h3>
                <p className="text-gray-600">Track your vaccination schedule</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/sun-exposure" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Sun Exposure Calculator</h3>
                <p className="text-gray-600">Calculate safe sun exposure time</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthCalculators;